import React, { useState } from "react";
import { FaUserEdit } from "react-icons/fa";
import {
  ModalBackdrop,
  FormModalBox,
  FormModalIcon,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalCancelBtn,
  ModalSubmitBtn,
  ModalFormGroup,
  ModalFormLabel,
  ModalFormInput,
  ModalFormError,
  ModalFieldsGrid,
  ModalGroupHeader,
  ModalOptionalText,
} from "./style";

export const FIELD_CONFIG = {
  dob: { label: "Date of Birth", type: "date", placeholder: "" },
  city: { label: "Current City", type: "text", placeholder: "e.g. Lagos" },
  stateOrigin: { label: "State of Origin", type: "text", placeholder: "e.g. Lagos" },
  phoneNo: { label: "Phone Number", type: "tel", placeholder: "e.g. 0800 000 0000" },
  address: { label: "Current Address", type: "text", placeholder: "e.g. 12 Main Street, Lagos" },
  principalName: { label: "School Principal Name", type: "text", placeholder: "e.g. Mr Alex" },
  schoolContactNo: { label: "School Contact No", type: "tel", placeholder: "e.g. 0800 000 0000" },
  schoolEmail: { label: "School Email", type: "email", placeholder: "e.g. school@example.com" },
  schoolAddress: { label: "School Address", type: "text", placeholder: "e.g. 12 School Road, Lagos" },
  schoolClass: { label: "Current Class in School", type: "text", placeholder: "e.g. JSS 1 or Grade 9" },
};

const SCHOOL_FIELDS = [
  "principalName",
  "schoolContactNo",
  "schoolEmail",
  "schoolAddress",
  "schoolClass",
];

export const AdditionalFormModal = ({ requiredFields, onSubmit, onClose }) => {
  const [form, setForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const getDobMaxDate = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 5);
    return today.toISOString().split('T')[0];
  };

  const allFilled = requiredFields.every(
    (field) => FIELD_CONFIG[field]?.optional || (form[field] || "").trim() !== ""
  );
  const generalFields = requiredFields.filter((field) => !SCHOOL_FIELDS.includes(field));
  const schoolFields = SCHOOL_FIELDS.filter((field) => requiredFields.includes(field));

  const handleChange = (field, value) => {
    setError(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allFilled || isSubmitting) return;
    try {
      setIsSubmitting(true);
      await onSubmit(form);
    } catch (err) {
      const raw =
        err?.response?.data?.errors ||
        err?.response?.data?.message ||
        "Something went wrong. Please try again.";
      setError(Array.isArray(raw) ? raw : [raw]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <FormModalBox onClick={(e) => e.stopPropagation()}>
        <FormModalIcon>
          <FaUserEdit />
        </FormModalIcon>
        <ModalTitle>Additional Information Required</ModalTitle>
        <ModalBody>
          This channel requires a little more information before you can join.
        </ModalBody>

        <form onSubmit={handleSubmit}>
          {error && (() => {
            const errors = [].concat(error);
            return (
              <ModalFormError>
                {errors.length === 1 ? errors[0] : (
                  <ul style={{ margin: 0, paddingLeft: "18px" }}>
                    {errors.map((msg, i) => <li key={i}>{msg}</li>)}
                  </ul>
                )}
              </ModalFormError>
            );
          })()}

          <ModalFieldsGrid>
            {generalFields.map((field) => (
              <FormField
                key={field}
                field={field}
                value={form[field] || ""}
                onChange={handleChange}
                dobMaxDate={getDobMaxDate()}
              />
            ))}

            {schoolFields.length > 0 && (
              <ModalGroupHeader>
                <strong>School verification</strong>
                <span>For verification purposes, the following information is required.</span>
              </ModalGroupHeader>
            )}

            {schoolFields.map((field) => (
              <FormField
                key={field}
                field={field}
                value={form[field] || ""}
                onChange={handleChange}
                dobMaxDate={getDobMaxDate()}
              />
            ))}
          </ModalFieldsGrid>

          <ModalActions style={{ marginTop: "24px" }}>
            <ModalCancelBtn type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </ModalCancelBtn>
            <ModalSubmitBtn type="submit" disabled={!allFilled || isSubmitting}>
              {isSubmitting ? "Joining…" : "Continue"}
            </ModalSubmitBtn>
          </ModalActions>
        </form>
      </FormModalBox>
    </ModalBackdrop>
  );
};

const FormField = ({ field, value, onChange, dobMaxDate }) => {
  const config = FIELD_CONFIG[field] || {
    label: field,
    type: "text",
    placeholder: "",
  };

  return (
    <ModalFormGroup $wide={field === "address"}>
      <ModalFormLabel htmlFor={`af-${field}`}>
        {config.label}
        {config.optional && <ModalOptionalText>Optional</ModalOptionalText>}
      </ModalFormLabel>
      <ModalFormInput
        id={`af-${field}`}
        type={config.type}
        value={value}
        onChange={(event) => onChange(field, event.target.value)}
        placeholder={config.placeholder}
        max={field === "dob" ? dobMaxDate : undefined}
        required={!config.optional}
      />
    </ModalFormGroup>
  );
};
